/**
 * web/src/games/catalogue.ts — the Games tab's sections and tiles
 * (card jT0odGfG7MVo, founder email 2026-10-31).
 *
 * One ordered catalogue, so the tab, the recommendation and the "waiting for
 * you" badge all read the same list. A game appears exactly once: Nocturne
 * ships two modes with opposite framing, so it is listed under Game On (Duel
 * is the case the lobby starts you in) and its Duet entry stays inside the
 * game — Team Up's copy names it. Two tiles for one game would make the
 * recommendation ambiguous and the rating card's keep/discard record
 * meaningless.
 */

export type GameId =
  | 'back-and-forth'
  | 'would-you-rather'
  | 'this-or-that'
  | 'guess-me'
  | 'half-a-map'
  | 'where-am-i'
  | 'guessmate'
  | 'nocturne';

/**
 * What a tile says before it starts (card aduRfcsTSaXW, founder email
 * 2026-10-31): what it is, how long, who goes first. The length and the first
 * move are read off each game's own rules — rounds, clue ladder, turn clock —
 * so the intro never promises a sitting the game does not actually have.
 */
export interface GameIntro {
  what: string;
  minutes: string;
  first: string;
}

export interface GameTile {
  id: GameId;
  title: string;
  blurb: string;
  intro: GameIntro;
  /** Back and forth opens on its own category chip; the quiz tiles open on theirs. */
  category?: string;
  /** The key the rating card (OJmoL0w1fGrm) records this game's verdict under,
   *  when it has one. Games without a key are never "discarded" — they have no
   *  verdict store at all, which is not the same as being liked. */
  ratingKey?: string;
}

export interface GameSection {
  key: 'quizzes' | 'team-up' | 'game-on';
  title: string;
  copy: string;
  games: GameTile[];
}

export const GAME_SECTIONS: GameSection[] = [
  {
    key: 'quizzes',
    title: 'Quizzes',
    copy: 'Learn something new about each other.',
    games: [
      {
        id: 'back-and-forth',
        title: 'Back and forth',
        blurb: 'One prompt, both answers, side by side once you have both written.',
      intro: {
        what: 'One prompt, both of you write an answer — you only see theirs once you have both locked in.',
        minutes: 'About two minutes a prompt.',
        first: 'Nobody moves first: you both write at the same time.',
      },
        category: 'places',
        ratingKey: 'places',
      },
      {
        id: 'would-you-rather',
        title: 'Would you rather',
        blurb: 'Silly, spicy or deep — two options, one pick each.',
      intro: {
        what: 'Two options, one pick each. Silly, spicy or deep.',
        minutes: 'About a minute a round.',
        first: 'Nobody moves first: you both pick at the same time.',
      },
        category: 'wyr-silly',
        ratingKey: 'would-you-rather',
      },
      {
        id: 'this-or-that',
        title: 'This or that',
        blurb: 'Quick picks that say a lot about the two of you.',
      intro: {
        what: 'Quick picks that say a lot about the two of you.',
        minutes: 'Ten seconds a pick.',
        first: 'Nobody moves first: you both pick at the same time.',
      },
        category: 'this-or-that',
        ratingKey: 'this-or-that',
      },
      {
        id: 'guess-me',
        title: 'Guess Me',
        blurb: 'One of you answers, the other has to guess what you said.',
      intro: {
        what: 'One of you answers a prompt, the other has to pick what you said out of three.',
        minutes: 'About three minutes a round.',
        first: 'The answerer writes first; the guesser only ever sees the question.',
      },
        ratingKey: 'memory',
      },
    ],
  },
  {
    key: 'team-up',
    title: 'Team Up',
    copy: 'Win together. Nocturne’s Duet case lives inside Nocturne, under Game On.',
    games: [
      {
        id: 'half-a-map',
        title: 'Half a Map',
        blurb: 'One of you walks blind, the other holds the map. Roles swap.',
      intro: {
        what: 'One of you walks blind, the other holds the map. Get to the exit together.',
        minutes: 'Three rounds, about ten minutes.',
        first: 'The Walker moves first — you swap roles every round.',
      },
      },
      {
        id: 'where-am-i',
        title: 'Where Am I',
        blurb: 'Both of you against the agent — one shared clue ladder.',
      intro: {
        what: 'Both of you against the concierge: find the place from three clues, obscure first.',
        minutes: 'Three clues, about five minutes.',
        first: 'Either of you can guess or uncover the next clue — no turn order.',
      },
      },
    ],
  },
  {
    key: 'game-on',
    title: 'Game On',
    copy: 'Bragging rights are on the line.',
    games: [
      {
        id: 'guessmate',
        title: 'Guessmate',
        blurb: 'The five-letter duel. Two secret words, two boards.',
      intro: {
        what: 'The five-letter duel. Two secret words, two boards, one winner.',
        minutes: 'Eight guesses each, two minutes a turn. Solve the word to win on the spot.',
        first: 'Whoever starts the match guesses first.',
      },
      },
      {
        id: 'nocturne',
        title: 'Nocturne',
        blurb: 'The two-screen deduction case — Duel against the Phantom, or Duet beside each other.',
      intro: {
        what: 'The two-screen deduction case — Duel against the Phantom, or Duet beside each other.',
        minutes: 'Four rounds, about fifteen minutes.',
        first: 'The Phantom hides first; the hunter opens the case.',
      },
      },
    ],
  },
];

/** Every tile in one flat list, so a lookup never walks the sections twice. */
export const ALL_GAMES: GameTile[] = GAME_SECTIONS.flatMap(s => s.games);

export function gameById(id: GameId): GameTile | undefined {
  return ALL_GAMES.find(g => g.id === id);
}

/** Deterministic day-seeded pick (card vsDgyzDbgtxh's rule, lifted to the tab):
 *  both partners see the same recommendation on the same day, it changes at
 *  local midnight, and it never lands on the same tile two days running when
 *  the pool allows it. Pure function of the calendar, no server state. */
export function dayPick<T>(pool: T[], now: number = Date.now()): T | null {
  if (pool.length === 0) return null;
  const hash = (day: number) => {
    let h = Math.imul(day ^ 0x9e3779b9, 0x85ebca6b);
    h ^= h >>> 13;
    return Math.abs(h);
  };
  const today = Math.floor(now / 86_400_000);
  let idx = hash(today) % pool.length;
  if (pool.length > 1) {
    const yesterday = hash(today - 1) % pool.length;
    if (yesterday === idx) idx = (idx + 1) % pool.length;
  }
  return pool[idx];
}

/**
 * Pear recommends: a game somebody has already started wins outright — that is
 * the one thing on the tab another person is waiting on — and a game whose next
 * move is mine beats one whose move is theirs. Otherwise a day-seeded pick over
 * games that are neither discarded nor already rated keep; when every game has
 * a verdict, the whole non-discarded pool is eligible again.
 */
export function recommendGame(
  ratings: Record<string, string> | null,
  started: Partial<Record<GameId, boolean>> = {},
  waiting: Partial<Record<GameId, boolean>> = {},
  now: number = Date.now(),
): GameTile | null {
  const mine = ALL_GAMES.find(g => waiting[g.id]);
  if (mine) return mine;
  const live = ALL_GAMES.find(g => started[g.id]);
  if (live) return live;
  const allowed = ALL_GAMES.filter(g => !g.ratingKey || ratings?.[g.ratingKey] !== 'discard');
  const unplayed = allowed.filter(g => !g.ratingKey || !(g.ratingKey in (ratings ?? {})));
  return dayPick(unplayed.length > 0 ? unplayed : allowed, now);
}
